import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { ArrowDown } from '../../svgIcons/ArrowDownIcon'
import { CloseIcon } from '../../svgIcons/CloseIcon'
import { useOnClickOutside } from '../../hooks/useClickOutside'
import { getTokenUSDPrice } from '../../blockchainUtils/math'
import { getWalletTokenAmount } from '../../services/amm'
import { useStoreActions, useStoreState } from '../../model/hooks'
import { getTokenBraavosName } from '../../blockchainUtils'
import { useWalletContext } from '../WalletContext'
import Popup from '../Popup'
import PrimaryButton from '../PrimaryButton'
import { XIcon } from '../../svgIcons/XIcon'
import { HelptipIcon } from '../../svgIcons/HelptipIcon'
import { SearchIcon } from '../../svgIcons/SearchIcon'
import { TokenOverview, loadTokenOverviewData } from '../charts/chartsApi'
import { TokenCharacteristicsContent } from '../TokenPage/TokenPage'
import { removeLeadingZeros } from '../../model/tokens/helper'
import { getERC20Details } from '../../services/amm_v3'
import { Network } from '../../services/token.service'
import UnknownToken from '../../svgIcons/icons/unknown_token.svg'
import { Token } from '../../atoms'
import Spinner from '../Spinner'

// export type Token = {
//     address: string
//     text: string
//     value: any
//     icon: string
//     symbol: string
//     decimals: number
//     id: number
//     name: string
//     is_bluechip: boolean
// }

function Select(props: {
    onSelect: (arg: any) => void
    placeholder: string
    selectedValue: any
    options: Token[]
    network: string
    isDisabled?: boolean
    size?: 'md' | 'sm'
    isCreatePool?: boolean
}) {
    const { onSelect, selectedValue, options, placeholder, network, isDisabled = false, size = 'md', isCreatePool = false } = props
    const [isSelectOpen, setIsSelectOpen] = useState(false)
    const [isAcceptUnknownToken, setIsAcceptUnknownToken] = useState<Token>()
    const [searchTerm, setSearchTerm] = useState('')
    const [viewableOptions, setViewableOptions] = useState([] as Token[])
    const [isInputFocused, setIsInputFocused] = useState(false)
    const [tokenOverView, setTokenOverView] = useState(undefined as unknown as TokenOverview)
    const [isLoading, setIsLoading] = useState(false)
    const setTokens = useStoreActions((actions) => actions.tokens.setTokens)

    const ref = useRef(null)
    useOnClickOutside(ref, () => {
        if (!isAcceptUnknownToken) setIsSelectOpen(false)
    })

    const prices = useStoreState((store) => store.pricing.prices)
    const { walletAddress: connectedAddress } = useWalletContext()

    useEffect(() => {
        if (!isSelectOpen) {
            setSearchTerm('')
        }
    }, [isSelectOpen])

    useEffect(() => {
        if (!searchTerm) setInitialState()
    }, [options])

    useEffect(() => {
        onSearch()
    }, [searchTerm])

    function setInitialState() {
        const initialOptions = [] as Token[]
        options.map((option) => {
            if (option.is_bluechip) initialOptions.push(option)
            // if (option.is_bluechip || network !== 'mainnet-alpha') initialOptions.push(option)
        })
        setViewableOptions(initialOptions)
    }

    async function onSearch() {
        if (!searchTerm) return setInitialState()
        const searchTermLowerCased = searchTerm.toLowerCase()
        const newViewableOptions = options.filter(
            (option) =>
                option.address.toLowerCase().includes(searchTermLowerCased) ||
                option.address.toLowerCase().includes(removeLeadingZeros(searchTermLowerCased)) ||
                option.name.toLowerCase().includes(searchTermLowerCased) ||
                option.symbol.toLowerCase().includes(searchTermLowerCased)
        )

        if (newViewableOptions && newViewableOptions.length > 0) setViewableOptions(newViewableOptions)
        else if (isCreatePool && searchTerm.length > 60 && searchTerm[1].toLowerCase() === 'x') {
            setIsLoading(true)
            const response = await getERC20Details(network as Network, searchTerm)
            if (response) {
                const token: Token = {
                    address: response?.address,
                    decimals: response?.decimals,
                    name: response?.name,
                    symbol: response?.symbol,
                    is_bluechip: false,
                    icon: UnknownToken,
                    id: 1,
                    network: network
                }

                const tokenData = { [token.address]: token } as Record<string, Token>
                setTokens({ network: network as Network, tokens: tokenData })
                setTimeout(() => {
                    setViewableOptions([token])
                    setIsLoading(false)
                }, 1000)
            } else {
                setViewableOptions([])
                setIsLoading(false)
            }
        }
    }

    function getTextSize(isSelected: boolean) {
        if (size === 'md') return `${isSelected ? 'text-def' : 'text-def'}`
        return `text-def`
    }

    function getIconSize() {
        if (size === 'md') return 32
        return 24
    }

    async function onSelectToken(token: Token, isForceUnkown = false) {
        const isUnknown = !token.is_bluechip
        if (isUnknown && !isForceUnkown && selectedValue.toLowerCase() !== token.address.toLowerCase() && network === 'mainnet-alpha') {
            const response = await loadTokenOverviewData(token.address.toLowerCase())
            setTokenOverView(response!)
            return setIsAcceptUnknownToken(token)
        }
        setIsSelectOpen(false)
        onSelect(token.address)
        setIsAcceptUnknownToken(undefined)
        setSearchTerm('')
    }

    function renderMenuItem(option: Token, isSelected = false) {
        let bgColor = 'rgba(255, 255, 255, 0.1)'
        if (isDisabled) bgColor = 'transparent'

        const isUnknown = !option.is_bluechip

        return (
            <div
                key={option.address}
                onClick={() => {
                    onSelectToken(option)
                }}
                style={{ padding: 0 }}>
                <div className='flex relative items-center justify-between flex items-center rounded-[30px] text-def lg:text-[24px] font-medium '>
                    <div
                        className={`${isDisabled ? 'bg-transparent cursor-not-allowed' : 'text-white'} flex items-center p-[2px] shrink-0`}
                        style={{ background: isSelected ? bgColor : '', borderRadius: '30px' }}>
                        <div className='flex shrink-0'>
                            <img className='mr-2 w-[24px] h-[24px] shrink-0' src={option.icon} alt='' width={getIconSize()} height={getIconSize()} />
                        </div>
                        <div className='flex flex-col text-4'>
                            <div className='flex'>
                                <span className={`${getTextSize(isSelected)}`}>{option.symbol}</span>
                                {isUnknown && !isSelected ? (
                                    <div className='relative ml-2 h-full text-[12px] text-[white] p-1 rounded-[8px]  flex items-end'>
                                        {/* <div className='ml-2 group inline-flex'> */}
                                        <HelptipIcon className='text-[#8387A5] w-[14px] h-[14px]' />
                                        {/* <span className='flex items-center justify-center w-[16px] h-[16px] rounded-full border-[1px] border-[#FF3D3D] text-[#FF3D3D]'>!</span> */}
                                        {/* <span className='left-[auto] bottom-[16px] w-[200px] group-hover:visible invisible group-hover:opacity-100 transition-opacity bg-[#242C58] text-sm text-gray-100 rounded-md absolute opacity-0  p-3 z-[1]'>
                                                {`This token is new and unknown so please conduct your own research before trading.`}
                                            </span> */}
                                        {/* </div> */}
                                    </div>
                                ) : null}
                            </div>
                            {isSelected ? null : <span className='font-normal text-gray text-[14px] lg:text-[16px]'>{option.name}</span>}
                        </div>
                        {isSelected && !isDisabled ? <ArrowDown className='ml-2 mr-2 h-4 w-4' /> : null}
                    </div>

                    {isSelected ? null : (
                        <span className='font-normal text-[18px]'>
                            {`$${getTokenUSDPrice(
                                Number(getWalletTokenAmount(network, option.address)) || 0,
                                prices && prices[getTokenBraavosName(option.symbol)] ? prices[getTokenBraavosName(option.symbol)].usd : 0
                            )}`}
                        </span>
                    )}
                </div>
            </div>
        )
    }

    function renderMenuItems() {
        if (isLoading)
            return (
                <div className='flex justify-center w-full mt-[24px]'>
                    <Spinner />
                </div>
            )

        if (viewableOptions && viewableOptions.length === 0)
            return (
                <div className='flex w-full justify-center items-center mt-[24px] font-bold'>
                    <span>No token found</span>
                </div>
            )
        const items = [] as ReactNode[]
        viewableOptions.forEach((value: Token) => {
            items.push(renderMenuItem(value))
        })
        return <div className='px-5 overflow-auto pr-6'>{items}</div>
    }

    function renderSelectedValue() {
        const optionsList: any[] = []
        options.forEach((item) => {
            optionsList.push(item)
        }) // TODO check how to do this in recoil or repalce recoil
        const value = optionsList.find((item: any) => item.address === selectedValue)

        return (
            <div onClick={() => (isDisabled ? null : setIsSelectOpen(!isSelectOpen))} className='select-none'>
                {value ? renderMenuItem(value, true) : <Spinner />}
            </div>
        )
    }

    function renderSearch() {
        return (
            <div className={`flex justify-between mt-6 w-full relative ${isInputFocused ? '#0AE8F9' : 'text-[#8387A5]'} px-5 text-[16px]`}>
                <div className='flex flex-col w-full'>
                    <span className='mb-6 font-600 text-[18px] text-white'>Select the token you want to swap or search for token and address, even if not listed below.</span>
                    <div className='relative flex items-center'>
                        <input
                            value={searchTerm}
                            onBlur={() => {
                                setIsInputFocused(false)
                            }}
                            onFocus={() => {
                                setIsInputFocused(true)
                            }}
                            onChange={(e) => {
                                setSearchTerm(e.target.value)
                            }}
                            className='pr-[32px] focus:border-[#0AE8F9] border-[1px] rounded-[8px] px-4 py-2  border-[#8387A5]  w-full h-full  outline-none bg-transparent'
                            placeholder='Search Token name or address'
                        />
                        <SearchIcon className='absolute right-[16px] top-[10px] w-[18px] h-[18px]' />
                    </div>
                </div>
            </div>
        )
    }

    function renderSelectionScreen() {
        if (!isSelectOpen) return null

        return (
            <div style={{ zIndex: 1000 }} className='fixed z-[10000] flex items-center justify-center z-[20] top-[100px] left-0 right-0 lg:w-full h-full h-[620px] max-h-[620px]'>
                {/* <div className="absolute w-full h-full bg-black" style={{ opacity: 0.7 }} /> */}
                <div ref={ref} style={{ border: '1px solid #0C3F6D' }} className='h-full py-5  mx-4 lg:mx-0 bg-[#010620] rounded-xl m-auto flex flex-col w-full lg:w-[528px]'>
                    <div className='w-full flex flex-col mb-5 border-b-[1px] border-[#0C3F6D] pb-6'>
                        <div className='px-5 flex justify-between'>
                            <span className='block text-[24px] font-bold text-white '>Select Token</span>
                            <CloseIcon className='mr-4 w-6 h-6 cursor-pointer' onClick={() => setIsSelectOpen(false)} />
                        </div>
                        {renderSearch()}
                    </div>
                    {renderMenuItems()}
                </div>
            </div>
        )
    }

    function renderAcceptUnkownToken() {
        if (!isAcceptUnknownToken) return null

        const token = viewableOptions.find((tok) => tok.address === isAcceptUnknownToken.address)
        if (!token) return null

        return (
            <Popup className='z-[100]' contentClassName='w-[528px]' open={Boolean(isAcceptUnknownToken)} onClose={() => setIsAcceptUnknownToken(undefined)}>
                <div className='relative flex flex-col items-center'>
                    <XIcon className='absolute right-[0px] top-[0px] cursor-pointer w-[16px] h-[16px]' onClick={() => setIsAcceptUnknownToken(undefined)} />
                    <div className='flex  flex-col items-center text-center w-full'>
                        <img className='rounded-full border-[1px] borer-white' src={token.icon} alt='' width={66} height={66} />
                        <div className='mt-2 flex flex-col items-center mb-2'>
                            <span className='text-[18px] text-white font-bold'>{token.symbol}</span>
                            <span className='text-[16px] text-[#8387A5]'>{token.name}</span>
                        </div>
                        {/* <span className='text-[18px] font-bold mt-[32px] mb-[42px]'>This token is new or unknown so please conduct your own research before trading.</span> */}
                        <TokenCharacteristicsContent
                            tokenCharacteristics={{
                                is_code_verified: tokenOverView ? tokenOverView.is_code_verified : 'unknown',
                                is_initial_liquidity_timelock: tokenOverView ? tokenOverView.is_initial_liquidity_timelock : 'unknown',
                                is_none_upgradable: tokenOverView ? tokenOverView.is_none_upgradable : 'unknown',
                                is_not_taxed: tokenOverView ? tokenOverView.is_not_taxed : 'unknown'
                            }}
                        />
                    </div>
                    <PrimaryButton className='w-full mt-4' onClick={() => onSelectToken(isAcceptUnknownToken, true)}>
                        <span>I understand</span>
                    </PrimaryButton>
                    <span
                        className='text-center mt-[16px] cursor-pointer lg:hover:underline'
                        onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setIsAcceptUnknownToken(undefined)
                        }}>
                        Cancel
                    </span>
                </div>
            </Popup>
        )
    }

    return (
        <div className='w-full relative cursor-pointer'>
            {selectedValue ? (
                renderSelectedValue()
            ) : (
                <span
                    onClick={() => setIsSelectOpen(!isSelectOpen)}
                    className={`select-none flex w-full justify-center items-center ${size === 'md' ? 'text-[18px]' : 'text-[18px]'} bg-[#366FB7] py-1 px-2 rounded-full`}>
                    {placeholder}
                    <ArrowDown className='ml-2 h-4 w-4' />
                </span>
            )}
            {isSelectOpen ? renderSelectionScreen() : null}
            {renderAcceptUnkownToken()}
        </div>
    )
}

export default Select
